import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const UserContext = createContext();

export const useUser = () => {
    return useContext(UserContext);
};

export const UserProvider = ({ children }) => {
    const [user, setUser] = useState(() => {
        const savedUser = localStorage.getItem('user');
        return savedUser ? JSON.parse(savedUser) : null;
    });

    const [settings, setSettings] = useState(() => {
        const savedSettings = localStorage.getItem('settings');
        return savedSettings ? JSON.parse(savedSettings) : {};
    });

    useEffect(() => {
        if (user) {
            localStorage.setItem('user', JSON.stringify(user));
            const savedSettings = localStorage.getItem('settings');
            if (savedSettings) {
                setSettings(JSON.parse(savedSettings));
            } else {
                const fetchSettings = async () => {
                    try {
                        const response = await fetch(`${process.env.REACT_APP_API_URI}/api/user-settings`, {
                            headers: { Authorization: user.api_key }
                        });
                        if (response.ok) {
                            const data = await response.json();
                            setSettings(data.settings || {});
                            localStorage.setItem('settings', JSON.stringify(data.settings || {}));
                        }
                    } catch (error) {
                        console.error('Fehler beim Abrufen der Benutzereinstellungen:', error.message);
                    }
                };
                fetchSettings();
            }
        } else {
            localStorage.removeItem('user');
            localStorage.removeItem('settings');
            setSettings({});
        }
    }, [user]);

    const updateSetting = async (key, value) => {
        // Use functional update to prevent race conditions
        let newSettings;
        setSettings(prev => {
            newSettings = { ...prev, [key]: value };
            localStorage.setItem('settings', JSON.stringify(newSettings));
            return newSettings;
        });

        try {
            await fetch(`${process.env.REACT_APP_API_URI}/api/user-settings`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: user.api_key },
                body: JSON.stringify({ settings: newSettings }) // Note: newSettings might be slightly stale if multiple updates happen very fast, but local state is correct
            });
        } catch (error) {
            console.error('Fehler beim Speichern der Benutzereinstellungen:', error.message);
        }
    };

    // Synchronisiert einen Setting-Wert nur im lokalen State + localStorage (ohne API-Call)
    // Wird verwendet wenn Settings bereits über eine andere API gespeichert wurden
    const syncSetting = useCallback((key, value) => {
        setSettings(prev => {
            const updated = { ...prev, [key]: value };
            localStorage.setItem('settings', JSON.stringify(updated));
            return updated;
        });
    }, []);

    // logout mit useCallback memoizen
    const logout = useCallback(() => {
        setUser(null);
        setSettings({});
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('settings');
    }, []);

    return (
        <UserContext.Provider value={{ user, setUser, logout, settings, updateSetting, syncSetting }}>
            {children}
        </UserContext.Provider>
    );
};